import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { editDataSource, renameDataSource } from '../root/thunks';
import {
  addDatasource,
  deleteDatasource,
  editDatasource,
  renameDatasource,
} from '../webforms/thunks';
import {
  addSharedSource,
  deleteSharedSource,
  editSharedSource,
  fetchSharedDatasources,
  updateSharedDatasources,
} from './thunks';

export type ISharedDatasourcesState = {
  state: 'loading' | 'loaded' | 'error';
  sources: { [key: string]: datasources.ICreateDataSource[] };
  error?: string;
};

const stateSlice = createSlice({
  name: 'shared-datasources',
  initialState: (): ISharedDatasourcesState => {
    return {
      state: 'loading',
      sources: {},
    };
  },
  reducers: {
    addEmptyNamespace(state, action: PayloadAction<{ name: string }>) {
      state.sources[action.payload.name] = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSharedDatasources.pending, (_state) => {
        return { ..._state, state: 'loading', sources: {} };
      })
      .addCase(fetchSharedDatasources.fulfilled, (_state, action) => {
        return {
          state: 'loaded',
          sources: action.payload,
        };
      })
      .addCase(fetchSharedDatasources.rejected, (_state, action) => {
        return {
          ..._state,
          state: 'error',
          sources: {},
          error: action.payload || 'Error while loading the shared datasources',
        };
      })
      .addCase(updateSharedDatasources.fulfilled, (_state, action) => {
        return {
          ..._state,
          state: 'loaded',
          sources: action.payload,
        };
      })
      .addCase(editDataSource.fulfilled, (state, action) => {
        const {
          payload: ds_array,
          meta: { arg: payload },
        } = action;

        if (!payload.namespace) return;

        Object.assign(state.sources, { [payload.namespace!]: ds_array });
      })
      .addCase(addSharedSource.fulfilled, (state, action) => {
        return {
          ...state,
          sources: action.payload,
        };
      })
      .addCase(deleteSharedSource.fulfilled, (state, action) => {
        return {
          ...state,
          sources: action.payload,
        };
      })
      .addCase(editSharedSource.fulfilled, (state, action) => {
        return {
          ...state,
          sources: action.payload,
        };
      })
      .addCase(renameDataSource.fulfilled, (state, action) => {
        if (!action.payload) return state;
        const { datasources, namespace } = action.payload;
        if (namespace && state.sources[namespace])
          Object.assign(state.sources[namespace], {
            [namespace]: datasources,
          });
      })
      .addMatcher(
        isAnyOf(
          addDatasource.fulfilled,
          deleteDatasource.fulfilled,
          editDatasource.fulfilled,
          renameDatasource.fulfilled,
        ),
        (state, action) => {
          if (!action.payload) return;
          const { namespace, list } = action.payload;
          if (!namespace) return;

          Object.assign(state.sources, { [namespace]: list });
        },
      );
  },
});

export const { addEmptyNamespace } = stateSlice.actions;

export default stateSlice;
